<template>
  <q-layout view="lHh Lpr lFf">
    <!-- Mobile top bar (hidden on md+) -->
    <q-header
      class="lt-md"
      :style="{
        background: 'var(--oa-bg-1)',
        borderBottom: '1px solid var(--oa-border)',
      }"
    >
      <q-toolbar style="min-height: 52px">
        <q-btn
          flat
          dense
          round
          icon="menu"
          :style="{ color: 'var(--oa-text-muted)' }"
          @click="drawerOpen = true"
        />
        <q-space />
        <router-link to="/" class="text-decoration-none">
          <span
            :style="{
              fontFamily: 'var(--oa-font-display)',
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--oa-gold-full)',
              letterSpacing: '1px',
            }"
          >
            OLYMPIC ACADEMY
          </span>
        </router-link>
        <q-space />
        <router-link to="/profile">
          <q-btn flat dense round icon="person" :style="{ color: 'var(--oa-text-muted)' }" />
        </router-link>
      </q-toolbar>
    </q-header>

    <!-- Sidebar drawer -->
    <q-drawer
      v-model="drawerOpen"
      show-if-above
      bordered
      :width="sidebarCollapsed ? 68 : 220"
      :style="{
        background: 'var(--oa-bg-1)',
        borderRight: '1px solid var(--oa-border)',
        transition: 'width 0.3s ease',
      }"
    >
      <!-- Logo section -->
      <div
        class="flex items-center justify-between"
        :style="{
          padding: '20px 16px 16px',
          borderBottom: '1px solid var(--oa-border)',
        }"
      >
        <router-link to="/" class="text-decoration-none flex items-center gap-3">
          <span
            v-if="!sidebarCollapsed"
            :style="{
              fontFamily: 'var(--oa-font-display)',
              fontSize: '22px',
              fontWeight: 600,
              color: 'var(--oa-gold-full)',
              letterSpacing: '1px',
            }"
          >
            Olympic Academy
          </span>
          <span
            v-else
            :style="{
              fontFamily: 'var(--oa-font-display)',
              fontSize: '18px',
              fontWeight: 700,
              color: 'var(--oa-gold-full)',
            }"
          >
            OA
          </span>
        </router-link>
        <q-btn
          v-if="$q.screen.lt.md"
          flat
          dense
          round
          icon="close"
          :style="{ color: 'var(--oa-text-disabled)' }"
          @click="drawerOpen = false"
        />
      </div>

      <!-- Navigation -->
      <q-scroll-area class="fit" style="padding-top: 12px; padding-bottom: 180px">
        <!-- Standalone: Inicio -->
        <router-link
          to="/"
          class="flex items-center text-decoration-none"
          :class="{ 'justify-center': sidebarCollapsed }"
          :style="{
            padding: sidebarCollapsed ? '10px 0' : '10px 16px',
            borderLeft: isActiveRoute('/') ? '2px solid var(--oa-gold-full)' : '2px solid transparent',
            background: isActiveRoute('/') ? 'var(--oa-active-bg)' : 'transparent',
            color: isActiveRoute('/') ? 'var(--oa-text-primary)' : 'var(--oa-text-secondary)',
            fontWeight: isActiveRoute('/') ? '500' : '400',
            fontSize: '13px',
            gap: '10px',
            transition: 'all 0.15s ease',
          }"
          @click="onNavClick"
        >
          <q-icon name="dashboard" size="16px" style="opacity: 0.7" />
          <span v-if="!sidebarCollapsed">Inicio</span>
        </router-link>

        <!-- Divider -->
        <div
          :style="{
            height: '1px',
            background: 'var(--oa-border)',
            margin: '8px 16px',
          }"
        />

        <!-- Nav groups -->
        <template v-for="group in visibleGroups" :key="group.id">
          <!-- Group header -->
          <button
            class="full-width flex items-center cursor-pointer"
            :class="{ 'justify-center': sidebarCollapsed }"
            :style="{
              padding: sidebarCollapsed ? '8px 0' : '8px 16px',
              border: 'none',
              background: 'transparent',
              color: groupHasActiveChild(group) ? 'var(--oa-gold-mid)' : 'var(--oa-text-disabled)',
              fontWeight: groupHasActiveChild(group) ? '500' : '400',
              fontSize: '11px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontFamily: 'var(--oa-font-body)',
              cursor: 'pointer',
              gap: '8px',
              transition: 'color 0.15s ease',
            }"
            @click="toggleGroup(group.id)"
          >
            <q-icon :name="group.icon" size="16px" />
            <span v-if="!sidebarCollapsed" class="flex-grow text-left">{{ group.label }}</span>
            <q-icon
              v-if="!sidebarCollapsed"
              name="expand_more"
              size="14px"
              :style="{
                transform: expandedGroupId === group.id ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.22s ease-in-out',
              }"
            />
          </button>

          <!-- Group items (expanded) -->
          <div
            v-if="expandedGroupId === group.id && !sidebarCollapsed"
            :style="{
              marginLeft: '16px',
              paddingLeft: '12px',
              borderLeft: '1px solid rgba(255,255,255,0.06)',
              marginTop: '2px',
              paddingBottom: '4px',
            }"
          >
            <router-link
              v-for="item in group.visibleItems"
              :key="item.to"
              :to="item.to"
              class="flex items-center text-decoration-none"
              :style="{
                padding: '6px 12px',
                borderRadius: 'var(--oa-radius-md)',
                fontSize: '13px',
                gap: '10px',
                transition: 'all 0.15s ease',
                marginBottom: '2px',
                background: isActiveRoute(item.to) ? 'rgba(232, 184, 75, 0.1)' : 'transparent',
                color: isActiveRoute(item.to) ? 'var(--oa-gold-full)' : 'var(--oa-text-secondary)',
                fontWeight: isActiveRoute(item.to) ? '500' : '400',
              }"
              @click="onNavClick"
            >
              <q-icon :name="item.icon" size="16px" style="opacity: 0.7" />
              <span>{{ item.label }}</span>
            </router-link>
          </div>

          <!-- Collapsed: show group items as icons only when that group is active -->
          <div v-if="sidebarCollapsed && groupHasActiveChild(group)" class="flex column items-center">
            <router-link
              v-for="item in group.visibleItems"
              :key="item.to"
              :to="item.to"
              class="flex items-center justify-center text-decoration-none"
              :style="{
                padding: '8px',
                borderRadius: 'var(--oa-radius-md)',
                color: isActiveRoute(item.to) ? 'var(--oa-gold-full)' : 'var(--oa-text-muted)',
              }"
              @click="onNavClick"
            >
              <q-icon :name="item.icon" size="16px" />
              <q-tooltip v-if="sidebarCollapsed" anchor="center right" self="center left">
                {{ item.label }}
              </q-tooltip>
            </router-link>
          </div>
        </template>
      </q-scroll-area>

      <!-- User section -->
      <div
        :style="{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          borderTop: '1px solid var(--oa-border)',
          background: 'var(--oa-bg-1)',
        }"
      >
        <router-link
          to="/profile"
          class="flex items-center text-decoration-none"
          :class="{ 'justify-center': sidebarCollapsed }"
          :style="{ gap: '10px' }"
          @click="onNavClick"
        >
          <!-- Avatar -->
          <div
            class="flex items-center justify-center shrink-0"
            :style="{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'var(--oa-bg-3)',
              border: '1.5px solid var(--oa-gold-dim)',
              fontFamily: 'var(--oa-font-body)',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--oa-gold-mid)',
            }"
          >
            {{ userInitials }}
          </div>
          <!-- User info (expanded only) -->
          <div v-if="!sidebarCollapsed" class="flex-grow" style="min-width: 0">
            <p
              class="ellipsis q-ma-none"
              :style="{
                fontSize: '13px',
                fontWeight: 500,
                color: 'var(--oa-text-primary)',
                lineHeight: '1.2',
              }"
            >
              {{ userName }}
            </p>
            <p
              class="q-ma-none"
              :style="{
                fontFamily: 'var(--oa-font-mono)',
                fontSize: '11px',
                color: 'var(--oa-gold-mid)',
                marginTop: '2px',
              }"
            >
              {{ auraDisplay }} AURA
            </p>
          </div>
        </router-link>

        <!-- Logout (expanded only) -->
        <button
          v-if="!sidebarCollapsed"
          class="flex items-center full-width cursor-pointer"
          :style="{
            marginTop: '8px',
            padding: '6px 12px',
            border: 'none',
            background: 'transparent',
            borderRadius: 'var(--oa-radius-md)',
            color: 'var(--oa-text-disabled)',
            fontSize: '12px',
            fontFamily: 'var(--oa-font-body)',
            gap: '8px',
            cursor: 'pointer',
            transition: 'color 0.15s ease',
          }"
          @click="handleLogout"
        >
          <q-icon name="logout" size="14px" />
          <span>Cerrar sesion</span>
        </button>
      </div>

      <!-- Collapse toggle (desktop only) -->
      <q-btn
        v-if="$q.screen.gt.sm"
        round
        dense
        flat
        :icon="sidebarCollapsed ? 'chevron_right' : 'chevron_left'"
        size="xs"
        :style="{
          position: 'absolute',
          right: '-12px',
          top: '28px',
          width: '24px',
          height: '24px',
          background: 'var(--oa-bg-1)',
          border: '1px solid var(--oa-border)',
          color: 'var(--oa-text-muted)',
          zIndex: 10,
        }"
        @click="sidebarCollapsed = !sidebarCollapsed"
      />
    </q-drawer>

    <!-- Page container -->
    <q-page-container>
      <ErrorBoundary>
        <router-view />
      </ErrorBoundary>
    </q-page-container>
  </q-layout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from 'stores/useAuthStore';
import { usePermissions } from 'src/composables/usePermissions';
import ErrorBoundary from 'src/components/ErrorBoundary.vue';

interface NavItem {
  label: string;
  icon: string;
  to: string;
  resource?: string;
}

interface NavGroup {
  id: string;
  label: string;
  icon: string;
  items: NavItem[];
}

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  aspirante: 'Aspirante',
  cc: 'Coach Certified',
  sc: 'Senior Coach',
  c1: 'Coach Nivel 1',
  c2: 'Coach Nivel 2',
  ot_candidato: 'OT Candidato',
  ot: 'Owner Trainer',
  kairos: 'Kairos',
  gj: 'GJ',
  lider_nacional: 'Lider Nacional',
  lider_regional: 'Lider Regional',
  evaluador: 'Evaluador',
  mentor: 'Mentor',
  admin: 'Administrador',
  super_admin: 'Super Admin',
};

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'formacion',
    label: 'Formacion',
    icon: 'school',
    items: [
      { label: 'Cursos', icon: 'shopping_bag', to: '/courses', resource: 'courses' },
      { label: 'Aprender', icon: 'menu_book', to: '/learn', resource: 'learn' },
      { label: 'El Ascenso', icon: 'terrain', to: '/ascenso', resource: 'ascenso' },
      { label: 'Certificacion', icon: 'card_membership', to: '/certificacion', resource: 'certificacion' },
      { label: 'Logros', icon: 'bolt', to: '/achievements', resource: 'achievements' },
      { label: 'Planificador', icon: 'checklist', to: '/planificador', resource: 'planificador' },
      { label: 'Comunidad', icon: 'groups', to: '/community', resource: 'community' },
    ],
  },
  {
    id: 'supervision',
    label: 'Supervision',
    icon: 'assignment',
    items: [
      { label: 'Evaluaciones', icon: 'assignment', to: '/evaluations', resource: 'evaluations' },
      { label: 'Instrumentos', icon: 'fact_check', to: '/evaluations/instruments', resource: 'evaluations' },
      { label: 'Evolucion', icon: 'trending_up', to: '/evaluations/evolution', resource: 'evolution' },
    ],
  },
  {
    id: 'operaciones',
    label: 'Operaciones',
    icon: 'store',
    items: [
      { label: 'Incorporacion', icon: 'directions_walk', to: '/incorporacion', resource: 'incorporacion' },
      { label: 'Guia Operativa', icon: 'explore', to: '/guia', resource: 'guia' },
      { label: 'Legal', icon: 'gavel', to: '/legal', resource: 'legal' },
      { label: 'Mi Sede', icon: 'store', to: '/sede', resource: 'sede' },
      { label: 'Dashboard OT', icon: 'bar_chart', to: '/ot', resource: 'ot' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: 'admin_panel_settings',
    items: [
      { label: 'Administracion', icon: 'settings', to: '/admin', resource: 'admin' },
    ],
  },
];

const STORAGE_KEY = 'oa_nav_expanded_group';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const { canAccess } = usePermissions();

const drawerOpen = ref(false);
const sidebarCollapsed = ref(false);
const expandedGroupId = ref<string | null>(null);

// Compute active group from route path
function getActiveGroupId(path: string): string | null {
  for (const group of NAV_GROUPS) {
    if (group.items.some((item) => path === item.to || path.startsWith(item.to + '/'))) {
      return group.id;
    }
  }
  return null;
}

function isActiveRoute(to: string): boolean {
  const path = route.path;
  if (to === '/') return path === '/';
  return path === to || path.startsWith(to + '/');
}

function groupHasActiveChild(group: { visibleItems: NavItem[] }): boolean {
  return group.visibleItems.some((item) => isActiveRoute(item.to));
}

function toggleGroup(groupId: string) {
  const next = expandedGroupId.value === groupId ? null : groupId;
  expandedGroupId.value = next;
  if (next) {
    localStorage.setItem(STORAGE_KEY, next);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function onNavClick() {
  // Close drawer on mobile after navigation
  if (window.innerWidth < 1024) {
    drawerOpen.value = false;
  }
}

const userInitials = computed(() => {
  if (!authStore.user) return '?';
  const first = authStore.user.firstName?.[0] ?? '';
  const last = authStore.user.lastName?.[0] ?? '';
  return (first + last).toUpperCase() || '?';
});

const userName = computed(() => {
  if (!authStore.user) return '';
  return authStore.user.displayName || `${authStore.user.firstName} ${authStore.user.lastName}`;
});

const roleLabel = computed(() => {
  const role = authStore.user?.role;
  if (!role) return '';
  return ROLE_LABELS[role] ?? role;
});

const auraDisplay = computed(() => {
  return (authStore.user?.xp ?? 0).toLocaleString();
});

const visibleGroups = computed(() => {
  return NAV_GROUPS
    .map((group) => ({
      ...group,
      visibleItems: group.items.filter(
        (item) => !item.resource || canAccess(item.resource),
      ),
    }))
    .filter((group) => group.visibleItems.length > 0);
});

// Restore expanded group on mount
onMounted(() => {
  const stored = localStorage.getItem(STORAGE_KEY);
  const activeGroup = getActiveGroupId(route.path);
  expandedGroupId.value = activeGroup ?? stored;
});

// Auto-expand group on route change
watch(
  () => route.path,
  (path) => {
    const activeGroup = getActiveGroupId(path);
    if (activeGroup) {
      expandedGroupId.value = activeGroup;
      localStorage.setItem(STORAGE_KEY, activeGroup);
    }
  },
);

async function handleLogout() {
  await authStore.logout();
  await router.push('/login');
}
</script>

<style scoped>
.text-decoration-none {
  text-decoration: none;
}

.shrink-0 {
  flex-shrink: 0;
}

.flex-grow {
  flex-grow: 1;
}

.cursor-pointer {
  cursor: pointer;
}

/* Hover effects for nav items */
button:hover {
  color: var(--oa-text-secondary) !important;
}
</style>
